/**
 * v5.11 A1.4 — AutoGates monitoring module.
 * Cross-deal listing of every `run_mode: auto` GateRun, split into
 * Awaiting Confirmation + Completed tabs. Read-only summary of which
 * mappings have auto-run-on-entry switched on.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useTerm } from "@/lib/terminology.jsx";
import { Bot, Loader2, CheckCircle2, AlertTriangle, Zap } from "lucide-react";

export default function AutoGates() {
  const nav = useNavigate();
  const term = useTerm();
  const [tab, setTab] = useState("awaiting_confirmation");
  const [runs, setRuns] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/gate-runs", { params: { run_mode: "auto", status: tab } })
      .then(({ data }) => setRuns(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"));
  }, [tab]);

  const loadRules = () => {
    api.get("/gate-stage-mappings").then(({ data }) => setMappings(data || []))
      .catch(() => setMappings([]));
    api.get("/gate-advancement-rules").then(({ data }) => setRules(data || []))
      .catch(() => setRules([]));
  };
  useEffect(loadRules, []);

  const toggleMapping = async (m) => {
    setBusy(m.mapping_id);
    try {
      await api.patch(`/gate-stage-mappings/${m.mapping_id}/auto-run`,
        { auto_run_on_entry: !m.auto_run_on_entry });
      toast.success(!m.auto_run_on_entry ? "Auto-run enabled" : "Auto-run disabled");
      loadRules();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const toggleRule = async (r) => {
    setBusy(r.rule_id);
    try {
      await api.patch(`/gate-advancement-rules/${r.rule_id}/toggle`,
        { is_active: !r.is_active });
      toast.success(!r.is_active ? "Auto-move enabled" : "Auto-move disabled");
      loadRules();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="p-8" data-testid="autogates-page">
      <div className="cw-caption uppercase tracking-widest">Automation</div>
      <h1 className="cw-h1 mt-1 inline-flex items-center gap-2">
        <Bot size={24} /> AutoGates
      </h1>
      <p className="text-sm text-[var(--cw-muted)] mt-1">
        AI-scored gate runs and automation state across every {term("deal").toLowerCase()}.
      </p>

      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <div className="cw-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="cw-caption uppercase">Auto-run mappings</div>
            <span className="text-xs text-[var(--cw-muted)]">
              {mappings.filter((m) => m.auto_run_on_entry).length} of {mappings.length} on
            </span>
          </div>
          {mappings.length === 0 ? (
            <div className="text-xs text-[var(--cw-muted)] italic">
              No gate-stage mappings exist yet.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[260px] overflow-y-auto">
              {mappings.map((m) => (
                <div key={m.mapping_id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md border"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid={`mapping-row-${m.mapping_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate">{m.gate_name}</div>
                    <div className="text-[10px] text-[var(--cw-muted)] truncate">
                      {m.pipeline_name} · {m.stage_name}
                    </div>
                  </div>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={!!m.auto_run_on_entry}
                      disabled={busy === m.mapping_id}
                      onChange={() => toggleMapping(m)}
                      data-testid={`mapping-toggle-${m.mapping_id}`} />
                    <span className="text-[10px]">{m.auto_run_on_entry ? "On" : "Off"}</span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="cw-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="cw-caption uppercase">Auto-move rules</div>
            <span className="text-xs text-[var(--cw-muted)]">
              {rules.filter((r) => r.is_active).length} of {rules.length} on
            </span>
          </div>
          {rules.length === 0 ? (
            <div className="text-xs text-[var(--cw-muted)] italic">
              No auto-move rules created yet.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[260px] overflow-y-auto">
              {rules.map((r) => (
                <div key={r.rule_id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md border"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid={`rule-row-${r.rule_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate">
                      {r.threshold_type === "all_pass" ? "All Pass" : `Score ≥ ${r.threshold_value}`}
                    </div>
                    <div className="text-[10px] text-[var(--cw-muted)] truncate">
                      Moves to {r.target_stage_id?.slice(0, 8) || "—"} · tag "{r.auto_move_tag_label || "Auto-Moved"}"
                    </div>
                  </div>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={!!r.is_active}
                      disabled={busy === r.rule_id}
                      onChange={() => toggleRule(r)}
                      data-testid={`rule-toggle-${r.rule_id}`} />
                    <span className="text-[10px]">{r.is_active ? "On" : "Off"}</span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 inline-flex rounded-md border overflow-hidden" style={{ borderColor: "var(--cw-border)" }}>
        {[
          { k: "awaiting_confirmation", l: "Awaiting Confirmation" },
          { k: "completed", l: "Completed" },
          { k: "failed", l: "Failed" },
        ].map((b) => (
          <button key={b.k} onClick={() => setTab(b.k)}
            className={`text-xs px-3 py-1.5 ${tab === b.k ? "font-semibold" : "text-[var(--cw-muted)]"}`}
            style={tab === b.k ? { background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" } : {}}
            data-testid={`autogates-tab-${b.k}`}>
            {b.l}
          </button>
        ))}
      </div>

      <div className="mt-3 cw-card overflow-hidden">
        {runs.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--cw-muted)]">
            No runs in this state.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead style={{ background: "rgba(40,42,116,0.05)" }}>
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Gate</th>
                <th className="px-4 py-3 font-semibold">{term("Deal")}</th>
                <th className="px-4 py-3 font-semibold">Triggered by</th>
                <th className="px-4 py-3 font-semibold">Started</th>
                <th className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r_) => (
                <tr key={r_.run_id}
                  className="border-t cursor-pointer hover:bg-[rgba(40,42,116,0.04)]"
                  style={{ borderColor: "var(--cw-border)" }}
                  onClick={() => nav(`/gate-runs/${r_.run_id}`)}
                  data-testid={`autogates-run-${r_.run_id}`}>
                  <td className="px-4 py-3 font-medium">{r_.gate_name}</td>
                  <td className="px-4 py-3">{r_.deal_name || "—"}</td>
                  <td className="px-4 py-3 text-[var(--cw-muted)] text-xs">
                    {r_.triggered_by_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-[var(--cw-muted)] text-xs">{r_.created_at}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={r_.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    awaiting_confirmation: { l: "Awaiting", bg: "rgba(243,191,92,0.20)", color: "#8A6510", Icon: AlertTriangle },
    completed: { l: "Completed", bg: "rgba(44,154,82,0.14)", color: "#1F6E3A", Icon: CheckCircle2 },
    running: { l: "Scoring", bg: "rgba(40,42,116,0.08)", color: "var(--cw-primary)", Icon: Loader2 },
    queued: { l: "Queued", bg: "rgba(40,42,116,0.08)", color: "var(--cw-primary)", Icon: Loader2 },
    failed: { l: "Failed", bg: "rgba(214,69,69,0.12)", color: "var(--cw-error)", Icon: AlertTriangle },
  }[status] || { l: status, bg: "rgba(0,0,0,0.05)", color: "var(--cw-muted)", Icon: Zap };
  const { Icon } = cfg;
  return (
    <span className="cw-badge inline-flex items-center gap-1" style={{ background: cfg.bg, color: cfg.color }}>
      <Icon size={11} /> {cfg.l}
    </span>
  );
}
